import {
  AfterViewInit,
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { CompareHelper, ConvertHelper } from '@barthauer/abc/core';
import {
  CacheContext,
  CacheExtentMode,
  CacheService,
  CacheStorageAdapter,
  DataService,
  ProgressState,
} from '@barthauer/abc/core/data';
import { ResourceManagerService } from '@barthauer/abc/core/resources';
import { MapService } from '@barthauer/abc/graphics/adapters/open-layers';
import { MessageBoxButtons, MessageMode, MessageSettings } from '@barthauer/abc/messages';
import { AbcWindowComponent } from '@barthauer/abc/ui-components';
import { AuthenticationInterceptor } from '@barthauer/basys/core/authentication';
import { ResourceSet } from 'libs/abc/core/resources/src/lib/resource-set';
import { MessageBoxService } from 'libs/abc/messages/src/lib/messageBox/messageBox.service';
import { Subject, Subscription } from 'rxjs';
import { concatMap, tap } from 'rxjs/operators';
import { BasysMapFrameService } from '../../basys-map-frame.service';
import { CordovaService } from '@barthauer/abc/core';

@Component({
  selector: 'app-cache-config',
  templateUrl: './cache-config.component.html',
  styleUrls: ['./cache-config.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false,
})
export class CacheConfigComponent implements AfterViewInit, OnInit, OnDestroy {
  public ConvertHelper = ConvertHelper;
  public ProgressState = ProgressState;
  public CacheStorageAdapter = CacheStorageAdapter;
  public tileLevelSource: any[];
  public tileLevel: number;
  public extentSource: object[];
  public extent: CacheExtentMode;
  public parentWindow: AbcWindowComponent;
  public currentConnectionType: string;
  public res = ResourceSet.Empty;
  public progressPercentage: number;
  private progressMessage: string;
  private subscriptions = new Array<Subscription>();
  private startCachingSubject = new Subject<number[]>();

  public offlineTimeoutSource: any[];
  public valueOfflineTimeout: number;
  private wasCachingStarted: boolean;
  private wasCacheDeleted: boolean;
  public frame: BasysMapFrameService;
  public get pictureResolution(): number {
    return this.cordovaService.pictureResolution;
  }
  public set pictureResolution(value: number) {
    this.cordovaService.pictureResolution = value;
  }
  public get pictureQuality(): number {
    return this.cordovaService.pictureQuality;
  }
  public set pictureQuality(value: number) {
    this.cordovaService.pictureQuality = value;
  }

  constructor(
    public cacheService: CacheService,
    public messageBoxService: MessageBoxService,
    private resourceService: ResourceManagerService,
    private mapService: MapService,
    private dataService: DataService,
    zone: NgZone,
    private cd: ChangeDetectorRef,
    appRef: ApplicationRef,
    public cordovaService: CordovaService
  ) {
    this.extent = 0;
    this.progressPercentage = 0;
    this.currentConnectionType = CacheStorageAdapter.onlineService.connectionType;
    this.cacheService.storage.updateServerAvailable();
    this.cacheService.estimateCacheUtilization();
    this.subscriptions.push(this.cacheService.onProgressChanged().subscribe(() => this.updateProgress()));
    this.subscriptions.push(
      this.startCachingSubject
        .asObservable()
        .pipe(
          concatMap((extent) =>
            this.mapService.saveLegendState().pipe(
              tap(() => {
                this.wasCachingStarted = true;
                this.cacheService.startCachingAsync(this.createCacheContext(extent));
              })
            )
          )
        )
        .subscribe(() => {})
    );
    this.subscriptions.push(
      CacheStorageAdapter.onlineService.isOnlineChanged.subscribe((online) => {
        appRef.tick();
        cd.detectChanges();
        zone.run(() => {
          this.currentConnectionType = '-';
          this.currentConnectionType = CacheStorageAdapter.onlineService.connectionType;
        });
      })
    );
    this.subscriptions.push(
      CacheStorageAdapter.onlineService.connectionTypeChanged.subscribe((type) => {
        appRef.tick();
        cd.detectChanges();
        zone.run(() => {
          this.currentConnectionType = '-';
          this.currentConnectionType = CacheStorageAdapter.onlineService.connectionType;
          this.cacheService.storage.updateServerAvailable();
        });
      })
    );

    this.subscriptions.push(
      CacheStorageAdapter.useOfflineModeChanged.subscribe(() => {
        cd.detectChanges();
      })
    );
  }

  private pictureResolutionChangedTimer: number;
  public pictureResolutionChanged(e) {
    this.pictureResolution = e.value;
    localStorage.setItem('resolutionValue', this.pictureResolution.toString());
    if (!this.pictureResolutionChangedTimer) {
      this.pictureResolutionChangedTimer = window.setTimeout(() => {
        clearTimeout(this.pictureResolutionChangedTimer);
        this.pictureResolutionChangedTimer = 0;
        this.dataService
          .executeSetValueQuery({ Name: 'resolutionValue', Value: this.pictureResolution.toString() })
          .subscribe();
      }, 1000);
    }
  }

  private pictureQualityChangedTimer: number;
  public pictureQualityChanged(e) {
    this.pictureQuality = e.value;
    localStorage.setItem('qualityValue', this.pictureQuality.toString());
    if (!this.pictureQualityChangedTimer) {
      this.pictureQualityChangedTimer = window.setTimeout(() => {
        clearTimeout(this.pictureQualityChangedTimer);
        this.pictureQualityChangedTimer = 0;
        this.dataService
          .executeSetValueQuery({ Name: 'qualityValue', Value: this.pictureQuality.toString() })
          .subscribe();
      }, 1000);
    }
  }

  async ngOnInit() {
    this.res = await this.resourceService.getResourceSet('basys-map-frame');
    this.fillSelectioData();
    this.fillTimeoutData();
  }

  ngAfterViewInit() {
    this.cacheService.resetProgress();
    //this.valueOfflineTimeout = this.getClientOfflineTimeout() ?? this.valueOfflineTimeout;
  }

  public getOnlineStateText(): string {
    if (this.cacheService.forceOfflineMode) {
      return this.res.get('CacheConfig-IsOffline');
    } else if (this.cacheService.isOnline) {
      if (this.cacheService.isServerAvailable) {
        if (!this.currentConnectionType)
          return this.res.get('CacheConfig-CurrectConnectionType', this.res.get('CacheConfig-IsOffline'));
        else return this.res.get('CacheConfig-CurrectConnectionType', this.currentConnectionType);
      } else return this.res.get('CacheConfig-ServerUnvailable');
    } else return this.res.get('CacheConfig-IsOffline');
  }

  private getClientOfflineTimeout(): number {
    return this.cacheService.getClientOfflineTimeout();
  }

  private getClientMaxZoomLevel(): number {
    return this.cacheService.getClientMaxZoomLevel();
  }

  private getClientCachedZoneOption(): number {
    return this.cacheService.getClientCachedZoneOption();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((element) => {
      element.unsubscribe();
    });
    if ((this.wasCachingStarted && CacheStorageAdapter.isDownloadComplete) || this.wasCacheDeleted) {
      AuthenticationInterceptor.reload();
    }
  }

  public async clearCache(): Promise<void> {
    this.wasCacheDeleted = true;
    await this.cacheService.clearCache();

    const settings = new MessageSettings();
    settings.mode = MessageMode.Info;
    settings.button = MessageBoxButtons.OK;
    settings.message = this.res.get('CacheConfig-ClearingCacheMessage');
    this.messageBoxService.showMessageBox(settings);
  }

  public changeExtentMode(value: any): void {
    if (value !== this.cacheService.getClientCachedZoneOption()) {
      this.extent = value;
      this.cacheService.setClientCachedZoneOption(value);
    }
  }

  public changeTileLevel(value: any): void {
    if (value !== this.cacheService.getClientMaxZoomLevel()) {
      this.tileLevel = value;
      this.cacheService.setClientMaxZoomLevel(value);
    }
  }

  public changeOfflineTimeout(value: any): void {
    if (value !== this.cacheService.getClientOfflineTimeout()) {
      this.cacheService.setClientOfflineTimeout(value);
      this.cacheService.storage.updateServerAvailable();
    }
  }

  private fillTimeoutData() {
    this.offlineTimeoutSource = new Array<object>();
    this.offlineTimeoutSource.push(
      {
        valueOfflineTimeout: 100,
        designation: '100',
      },
      {
        valueOfflineTimeout: 250,
        designation: '250',
      },
      {
        valueOfflineTimeout: 500,
        designation: '500',
      },
      {
        valueOfflineTimeout: 1000,
        designation: '1000',
      },
      {
        valueOfflineTimeout: 2000,
        designation: '2000',
      }
    );
    const defaultMilliseconds = this.cacheService.defaultClientPingTimeoutMilliseconds;

    if (this.cacheService.getClientOfflineTimeout()) {
      this.valueOfflineTimeout = this.cacheService.getClientOfflineTimeout();
    } else if (defaultMilliseconds) {
      this.offlineTimeoutSource.push({
        valueOfflineTimeout: 0,
        designation: defaultMilliseconds + ' (serverseitig konfiguriert)',
      });
      this.valueOfflineTimeout = 0;
    } else {
      this.valueOfflineTimeout = 500;
    }
  }

  private fillSelectioData() {
    this.tileLevelSource = new Array<object>();
    this.tileLevel = 12;
    const maxZoom = this.cacheService.getMaxZoom();
    for (let index = maxZoom; index > 11; index--) {
      let designation = index.toString();
      switch (index) {
        case maxZoom:
          designation = this.res.get('CacheConfig-ZoomMaxLevelDetail', designation);
          this.tileLevel = index;
          break;
        case 12:
          designation = this.res.get('CacheConfig-ZoomMinLevelDetail', designation);
          break;
        case 17:
          designation = this.res.get('CacheConfig-ZoomRecommendedLevelDetail', designation);
          this.tileLevel = index;
          break;
      }
      this.tileLevelSource.push({
        tileLevel: index,
        designation: designation,
      });
    }
    if (this.tileLevelSource.length === 0) {
      this.tileLevel = -1;
      this.tileLevelSource.push({
        tileLevel: this.tileLevel,
        designation: this.res.get('CacheConfig-TileLevelNotAvailable'),
      });
    } else if (this.getClientMaxZoomLevel()) {
      this.tileLevel = this.getClientMaxZoomLevel();
    }

    this.extentSource = new Array<object>();
    this.extentSource.push(
      {
        id: 0,
        designation: this.res.get('CacheConfig-ExtentSourceCurrentMapSection'),
      },
      {
        id: 1,
        designation: this.res.get('CacheConfig-ExtentSourceInitialMapSection'),
      },
      {
        id: 2,
        designation: this.res.get('CacheConfig-ExtentSourceCustomMapSection'),
      }
    );

    if (this.getClientCachedZoneOption()) {
      this.extent = this.getClientCachedZoneOption();
    }
  }

  public get inputFieldsDisabled(): boolean {
    return this.cacheService.progressState !== ProgressState.Idle;
  }

  public handleCaching() {
    if (this.cacheService.progressState !== ProgressState.Caching) {
      if (this.extent === CacheExtentMode.UserExtent) {
        this.selectCachingExtentOnMap();
      } else {
        this.startCachingSubject.next(null);
      }
    } else {
      this.cacheService.stopCaching();
    }
  }

  private createCacheContext(selectedExtent: number[]): CacheContext {
    return new CacheContext(this.cacheService.storage, this.tileLevel, this.extent, selectedExtent);
  }

  public selectCachingExtentOnMap() {
    this.toggleSettingDialogVisibility(false);
    this.mapService.selectExtent();
    setTimeout(() => {
      this.mapService.sendMapShortMessage(this.res.get('CacheConfig-MapShortMessage'));
    }, 200);
    this.mapService.onExtentSelected().subscribe((extent) => {
      this.toggleSettingDialogVisibility(true);
      this.startCachingSubject.next(extent);
    });
  }

  public toggleSettingDialogVisibility(visible: boolean) {
    if (this.parentWindow) {
      if (visible) this.parentWindow.maximizeDialog();
      else this.parentWindow.minimizeDialog();
    }
  }

  public statusFormat = (ratio) => {
    const percent: number = ratio * 100;
    const percentText: string = percent.toFixed(0);
    let text: string = this.res.get('CacheConfig-ProgressPercentage', percentText);

    const settings = new MessageSettings();
    settings.mode = MessageMode.Info;
    settings.button = MessageBoxButtons.OK;
    settings.message = this.res.get('CacheConfig-CompletedMessage');

    if (percentText == '100') {
      this.messageBoxService.showMessageBox(settings);
    }
    if (this.progressMessage) {
      text += ' ' + this.progressMessage;
    }
    return text;
  };

  private updateProgress() {
    //HACK: To update progress message when no progress percentage has changed
    if (
      (this.progressPercentage !== undefined && this.progressPercentage === this.cacheService.progressRatio) ||
      this.cacheService.progressState === ProgressState.Cancelling
    ) {
      this.progressPercentage += 0.00001;
    } else {
      this.progressPercentage = this.cacheService.progressRatio;
    }

    switch (this.cacheService.progressState) {
      case ProgressState.Caching:
        this.progressMessage = this.res.get('CacheConfig-ProgressMessageCaching');
        break;
      case ProgressState.Cancelling:
        this.progressMessage = this.res.get('CacheConfig-ProgressMessageCancelling');
        break;
      case ProgressState.Idle:
        this.progressMessage = null;
        break;
    }
  }
}
